import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useNotifications } from '../../contexts/NotificationContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import apiService from '../../services/api'
import {
  HiOutlineUsers,
  HiOutlineFolder,
  HiOutlineDocument,
  HiOutlineClock,
  HiOutlineUserGroup,
  HiOutlineCloudArrowUp,
  HiOutlineBell,
  HiOutlinePlus,
  HiOutlineArrowUpRight,
  HiOutlineCog6Tooth,
} from 'react-icons/hi2'
import {
  Badge,
  Banner,
  Button,
  Card,
  PageHeader,
  StatCard,
  Spinner,
} from '../../components/ui'
import SessionCalendar from '../../components/dashboard/SessionCalendar'
import ConnectionRequests from '../../components/dashboard/ConnectionRequests'
import QuickActions from '../../components/dashboard/QuickActions'

function formatStorageSize(sizeInMB) {
  if (!sizeInMB) return '0 MB'
  if (sizeInMB >= 1024) return `${(sizeInMB / 1024).toFixed(1)} GB`
  return `${sizeInMB.toFixed(1)} MB`
}

export default function OwnerDashboard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { unreadCount } = useNotifications()
  const { getEntity } = useWorkspace()
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        const response = await apiService.get('/dashboard/summary/')
        if (cancelled) return
        if (response.success) {
          setDashboardData(response.data.data)
          setError(null)
        } else {
          setError(response.error?.message || 'Failed to load dashboard data')
        }
      } catch {
        if (!cancelled) setError('Failed to load dashboard data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  if (loading) return <DashboardSkeleton />
  if (error) {
    return (
      <div className="space-y-4">
        <Banner tone="danger" title={t('common.networkError')}>{error}</Banner>
        <Button onClick={() => window.location.reload()}>{t('common.retry')}</Button>
      </div>
    )
  }

  const stats = [
    {
      label: t('dashboard.stats.totalCases', { entity: getEntity('case').plural }),
      value: dashboardData?.total_cases || 0,
      icon: HiOutlineFolder,
      tone: 'info',
      href: '/cases',
    },
    {
      label: t('dashboard.stats.totalClients', { entity: getEntity('client').plural }),
      value: dashboardData?.total_clients || 0,
      icon: HiOutlineUsers,
      tone: 'success',
      href: '/clients',
    },
    {
      label: t('dashboard.stats.totalSessions', { entity: getEntity('session').plural }),
      value: dashboardData?.total_sessions || 0,
      icon: HiOutlineClock,
      tone: 'brand',
      href: '/sessions',
    },
    {
      label: t('dashboard.stats.totalDocuments', { entity: getEntity('document').plural }),
      value: dashboardData?.total_documents || 0,
      icon: HiOutlineDocument,
      tone: 'warning',
      href: '/documents',
    },
    {
      label: t('dashboard.stats.connectedConnections', { entity: getEntity('connection').plural }),
      value: dashboardData?.connected_employees_count || 0,
      icon: HiOutlineUserGroup,
      tone: 'info',
      href: '/connections',
    },
    {
      label: t('dashboard.stats.storageUsed'),
      value: formatStorageSize(dashboardData?.space_used_mb),
      help: t('dashboard.stats.ofLimit', { percent: dashboardData?.space_used_percentage || 0 }),
      icon: HiOutlineCloudArrowUp,
      tone: 'brand',
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t('dashboard.eyebrow')}
        title={t('dashboard.welcome', { name: user?.name || '' })}
        subtitle={t('dashboard.subtitle')}
        actions={
          unreadCount > 0 ? (
            <Badge tone="warning" variant="soft" size="md">
              <HiOutlineBell className="w-3.5 h-3.5" />
              {t('dashboard.unreadNotifications', { count: unreadCount })}
            </Badge>
          ) : null
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map((stat) => <StatCard key={stat.label} {...stat} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <SessionCalendar />
          <QuickActions />
        </div>

        <div className="space-y-6">
          <ConnectionRequests />

          <Card padding="md">
            <Card.Header title={t('dashboard.accountManagement.title')} />
            <div className="space-y-2">
              <RowAction
                label={t('dashboard.accountManagement.invite')}
                icon={HiOutlinePlus}
                onClick={() => navigate('/connections?action=invite')}
              />
              <RowAction
                label={t('dashboard.accountManagement.settings')}
                icon={HiOutlineCog6Tooth}
                onClick={() => navigate('/profile-settings')}
              />
              <RowAction
                label={t('dashboard.accountManagement.storage')}
                icon={HiOutlineCloudArrowUp}
                onClick={() => navigate('/profile-settings?tab=subscription')}
              />
            </div>
          </Card>
        </div>
      </div>
    </div>
  )
}

function RowAction({ label, icon: Icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-ink-secondary hover:text-ink-primary hover:bg-muted transition-colors group"
    >
      <span className="flex items-center gap-2.5">
        {Icon && <Icon className="w-4 h-4 text-ink-muted group-hover:text-brand transition-colors" />}
        {label}
      </span>
      <HiOutlineArrowUpRight className="w-3.5 h-3.5 text-ink-muted group-hover:text-brand transition-colors rtl:rotate-[-90deg]" />
    </button>
  )
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="h-6 w-1/3 bg-muted rounded animate-pulse" />
        <div className="h-4 w-1/2 bg-muted rounded animate-pulse" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-28 bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 h-96 bg-muted rounded-xl animate-pulse" />
        <div className="h-96 bg-muted rounded-xl animate-pulse" />
      </div>
      <div className="flex justify-center pt-4">
        <Spinner />
      </div>
    </div>
  )
}
